import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { sendCode, verifyCode, type VerifyCodeResponse } from '@/api/endpoints/auth'
import { normalizeApiError } from '@/api/errors'
import { ERROR_SCOPES, INVALID_EMAIL_MESSAGE, OTP_NEEDS_NEW_CODE, resolveErrorMessage } from '@/constants/errorMessages'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import { isValidEmail, normalizeEmail } from '@/utils/validators'

// Состояние двухшагового входа (e-mail → OTP). Шаги — разные роуты (/login и /login/code),
// поэтому e-mail и таймер повторной отправки живут в сторе, а не в компоненте.
// В query их класть нельзя: afterEach отправляет fullPath в Яндекс.Метрику (router/guards.ts).
//
// Стор держит только ПОДТВЕРЖДЁННЫЙ шаг: e-mail и согласие попадают сюда, когда код
// реально отправлен (requestCode). Незаконченный ввод — черновик LoginView: он обязан
// исчезать вместе со страницей, иначе возвращается в поле после ухода и возврата.

export const CODE_LENGTH = 6
/** Фолбэк кулдауна: применяется только если бэк не прислал retry_after. */
export const RESEND_SECONDS = 180
/** Дольше этого шаг ввода кода не восстанавливаем: OTP на бэке уже точно мёртв. */
const STEP_TTL_MS = 15 * 60 * 1000

/** Снимок шага входа в sessionStorage — см. persist/hydrate. */
interface PersistedLoginStep {
	email: string
	consent: boolean
	/** Дедлайн кулдауна, ms epoch. 0 — кулдауна нет. */
	resendUntil: number
	savedAt: number
}

export const useLoginStore = defineStore('login', () => {
	const email = ref('')
	const consent = ref(false)
	const emailError = ref('')
	const codeError = ref('')
	// Ошибка входа через провайдера: ставит /auth/callback, показывает LoginView.
	// provider пустой — бэк не сказал, какой вход упал, текст идёт над всем блоком.
	const oauthError = ref('')
	const oauthErrorProvider = ref('')
	const sending = ref(false)
	const verifying = ref(false)
	const resendLeft = ref(0)
	// Дедлайн кулдауна (ms epoch) — источник истины для resendLeft. Хранить сам остаток
	// нельзя: он не переживает перезагрузку, а восстановленный «как есть» дал бы полный
	// срок заново, то есть F5 продлевал бы кулдаун до бесконечности.
	const resendUntil = ref(0)
	// После неудачной попытки автоотправка кода выключается: иначе правка одной цифры
	// слала бы новый запрос на каждый ввод и сжигала попытки на стороне бэка.
	const autoSubmitBlocked = ref(false)

	let resendTimerId: ReturnType<typeof setInterval> | null = null

	const canResend = computed(() => resendLeft.value === 0 && !sending.value)

	/**
	 * Можно ли отправлять форму. Функция от значений, а не computed от стора: адрес и
	 * согласие до успешной отправки — черновик LoginView, в сторе их ещё нет. Правило
	 * остаётся здесь, чтобы кнопка и requestCode не разошлись в понимании «валидно».
	 */
	function canSubmit(value: string, terms: boolean): boolean {
		return isValidEmail(value) && terms && !sending.value
	}

	const resendLabel = computed(() => {
		if (resendLeft.value <= 0) return ''
		const minutes = Math.floor(resendLeft.value / 60)
		const seconds = resendLeft.value % 60
		return `${minutes}:${String(seconds).padStart(2, '0')}`
	})

	function stopResendTimer() {
		if (resendTimerId !== null) {
			clearInterval(resendTimerId)
			resendTimerId = null
		}
	}

	/** Пересчёт остатка из дедлайна. Тик только считает — сам по себе он ничего не убавляет. */
	function syncResendLeft() {
		const left = Math.ceil((resendUntil.value - Date.now()) / 1000)
		resendLeft.value = left > 0 ? left : 0
		if (resendLeft.value === 0) stopResendTimer()
	}

	function startResendTimer(seconds: number = RESEND_SECONDS) {
		stopResendTimer()
		resendUntil.value = Date.now() + seconds * 1000
		syncResendLeft()
		// Считаем от реального времени, а не декрементом: в фоновой вкладке и на спящем
		// устройстве тики режутся, и отсчёт разошёлся бы с кулдауном на бэке.
		resendTimerId = setInterval(syncResendLeft, 1000)
		persist()
	}

	/**
	 * Полное снятие кулдауна (код умер — нужен новый). Дедлайн тоже обнуляем и пишем в
	 * storage: иначе перезагрузка вернула бы кнопку повтора под таймер.
	 */
	function clearResendCooldown() {
		stopResendTimer()
		resendUntil.value = 0
		resendLeft.value = 0
		persist()
	}

	// ---- persist / hydrate ---------------------------------------------------
	// Шаг входа должен переживать F5: без снимка гард requiresLoginEmail (router/guards.ts)
	// уводит на /login, хотя код из письма ещё жив. consent сохраняем не ради галочки в форме,
	// а потому что повторная отправка идёт через requestCode -> sendCode(email, consent):
	// после перезагрузки она ушла бы с terms: false. sessionStorage, а не localStorage:
	// e-mail персональный, и шаг входа не должен воскресать в другой вкладке через неделю.
	function persist() {
		if (!email.value) {
			clearPersisted()
			return
		}
		const snapshot: PersistedLoginStep = {
			email: email.value,
			consent: consent.value,
			resendUntil: resendUntil.value,
			savedAt: Date.now()
		}
		try {
			sessionStorage.setItem(STORAGE_KEYS.LOGIN_STEP, JSON.stringify(snapshot))
		} catch {
			/* sessionStorage может быть недоступен — молча игнорируем */
		}
	}

	function clearPersisted() {
		try {
			sessionStorage.removeItem(STORAGE_KEYS.LOGIN_STEP)
		} catch {
			/* см. persist */
		}
	}

	function hydrate() {
		try {
			const raw = sessionStorage.getItem(STORAGE_KEYS.LOGIN_STEP)
			if (!raw) return
			const saved: PersistedLoginStep | null = JSON.parse(raw)
			// Протухший снимок не воскрешаем: пустили бы на шаг ввода заведомо мёртвого кода.
			if (!saved?.email || Date.now() - saved.savedAt > STEP_TTL_MS) {
				clearPersisted()
				return
			}
			email.value = saved.email
			consent.value = !!saved.consent
			resendUntil.value = saved.resendUntil ?? 0
			// Остаток считаем от дедлайна, а не берём сохранённым: иначе каждая перезагрузка
			// начинала бы отсчёт заново.
			if (resendUntil.value > Date.now()) {
				syncResendLeft()
				resendTimerId = setInterval(syncResendLeft, 1000)
			}
		} catch {
			/* повреждённый JSON — начинаем шаг заново */
			clearPersisted()
		}
	}
	// Флаги sending/verifying и тексты ошибок сознательно не восстанавливаем: verifying: true
	// после F5 навсегда задизейблил бы кнопку, а старая ошибка всплыла бы без действий человека.
	hydrate()

	/**
	 * Уход из поля e-mail. Ошибку формата показываем именно здесь, а не на вводе:
	 * иначе красный текст мигал бы на первом же символе. Кнопка при невалидном
	 * адресе заблокирована, и blur (на мобиле — тап по чекбоксу) объясняет, почему.
	 *
	 * Значение приходит из черновика LoginView; дефолт — подтверждённый шаг из стора.
	 */
	function touchEmail(value: string = email.value) {
		// Пустое поле ошибкой не помечаем — человек мог просто ткнуть мимо.
		if (value.trim() && !isValidEmail(value)) emailError.value = INVALID_EMAIL_MESSAGE
	}

	function clearEmailError() {
		emailError.value = ''
	}

	/**
	 * Отправка кода на e-mail. true — код ушёл, можно переходить на шаг ввода кода.
	 *
	 * Адрес и согласие приходят из черновика LoginView. Дефолты — состояние стора:
	 * так работает повторная отправка со шага кода, где черновика уже нет.
	 */
	async function requestCode(
		draftEmail: string = email.value,
		draftConsent: boolean = consent.value
	): Promise<boolean> {
		// Нормализуем, а не только обрезаем: на бэк должен уехать тот же ключ, по которому
		// он считает лимит на адрес (см. normalizeEmail).
		const value = normalizeEmail(draftEmail)
		emailError.value = ''
		// Кнопка при невалидном адресе заблокирована, но остаются Enter и прямой вызов.
		if (!isValidEmail(value)) {
			emailError.value = INVALID_EMAIL_MESSAGE
			return false
		}
		sending.value = true
		try {
			// terms обязателен: согласие подтверждается на бэке, а не только чекбоксом.
			// TERMS_NOT_ACCEPTED показываем в emailError — единственный инлайн-слот на экране;
			// ошибка защитная, canSubmit и так не даёт отправить форму без согласия.
			const response = await sendCode(value, draftConsent)
			// sent: false без ошибки — код не ушёл, на шаг ввода пускать нельзя.
			if (response?.sent === false) {
				emailError.value = ERROR_SCOPES.authSendCode.fallback
				return false
			}
			// Шаг подтверждён: только теперь черновик становится состоянием флоу. Раньше нельзя —
			// адрес неудачной попытки воскресал бы в поле после ухода со страницы и возврата.
			// Каноничный вид: он же уедет во verify вторым шагом.
			email.value = value
			consent.value = draftConsent
			codeError.value = ''
			autoSubmitBlocked.value = false
			// Кулдаун диктует бэк; константа — только если поля в ответе нет.
			startResendTimer(response?.retry_after ?? RESEND_SECONDS)
			return true
		} catch (error) {
			emailError.value = resolveErrorMessage(error, ERROR_SCOPES.authSendCode)
			// Бэк может сам сказать, через сколько можно повторить — уважаем это значение.
			const retryAfter = normalizeApiError(error).retryAfter
			if (retryAfter) startResendTimer(retryAfter)
			return false
		} finally {
			sending.value = false
		}
	}

	/**
	 * Проверка кода. Возвращает ответ бэка или null (текст ошибки — в codeError).
	 * Отдаём весь ответ, а не только токен: по user.onboarding_completed экран входа
	 * выбирает цель редиректа и обходится без дополнительного GET /user.
	 */
	async function confirmCode(code: string): Promise<VerifyCodeResponse | null> {
		codeError.value = ''
		verifying.value = true
		try {
			// Тело плоское — токен лежит в корне, обёртки data у этой ручки нет.
			const response = await verifyCode(email.value, code)
			if (!response?.token) {
				autoSubmitBlocked.value = true
				codeError.value = ERROR_SCOPES.authVerifyCode.fallback
				return null
			}
			return response
		} catch (error) {
			autoSubmitBlocked.value = true
			codeError.value = resolveErrorMessage(error, ERROR_SCOPES.authVerifyCode)
			// Код мёртв (истёк / сожжены попытки) — снимаем кулдаун, иначе совет
			// «получите новый код» невыполним: кнопка повтора ещё под таймером.
			const failedCode = normalizeApiError(error).code
			if (failedCode && (OTP_NEEDS_NEW_CODE as readonly string[]).includes(failedCode)) clearResendCooldown()
			return null
		} finally {
			verifying.value = false
		}
	}

	function clearCodeError() {
		codeError.value = ''
	}

	function setOauthError(message: string, provider = '') {
		oauthError.value = message
		oauthErrorProvider.value = provider
	}

	function clearOauthError() {
		setOauthError('')
	}

	function reset() {
		stopResendTimer()
		email.value = ''
		consent.value = false
		emailError.value = ''
		codeError.value = ''
		clearOauthError()
		sending.value = false
		verifying.value = false
		resendLeft.value = 0
		resendUntil.value = 0
		autoSubmitBlocked.value = false
		clearPersisted()
	}

	return {
		email,
		consent,
		emailError,
		codeError,
		oauthError,
		oauthErrorProvider,
		sending,
		verifying,
		resendLeft,
		autoSubmitBlocked,
		canSubmit,
		canResend,
		resendLabel,
		touchEmail,
		clearEmailError,
		requestCode,
		confirmCode,
		clearCodeError,
		setOauthError,
		clearOauthError,
		startResendTimer,
		stopResendTimer,
		reset
	}
})
